import './Balsuite.css';
import logo from '../assets/logo.png';

// ─── DATA ────────────────────────────────────────────────────────────────────

const features = [
  {
    icon: '🧑‍💼', title: 'Employee Onboarding',
    desc: 'Digitise the entire onboarding journey — documents, role setup, team introductions, and tool access — so every new hire hits the ground running from day one.',
    tag: 'Day-1 ready', dark: false,
  },
  {
    icon: '⏱️', title: 'Timesheet Management',
    desc: 'Log hours effortlessly with daily, weekly, or project-based timesheets. Auto-track billable vs non-billable hours with one-click submissions and manager approvals.',
    tag: 'Auto-tracking', dark: false,
  },
  {
    icon: '📁', title: 'Project Management',
    desc: 'Plan, execute, and deliver projects on time. Milestones, dependencies, Gantt views, and real-time progress tracking — all in a single workspace.',
    tag: 'On-time delivery', dark: false,
  },
  {
    icon: '✅', title: 'Work Management',
    desc: 'Assign tasks, set priorities, track deadlines, and collaborate in context. From individual to-dos to complex team workflows — keep everything moving.',
    tag: 'Zero missed tasks', dark: false,
  },
  {
    icon: '📊', title: 'Resource Utilization',
    desc: 'See exactly who is overloaded, underutilised, or available. Balance workloads across teams, prevent burnout, and maximise team capacity intelligently.',
    tag: 'Capacity insights', dark: false,
  },
  {
    icon: '🏃', title: 'Sprint Management',
    desc: 'Run agile sprints with ease. Plan backlogs, create sprint boards, track velocity, and ship iteratively with built-in retrospective and burndown charts.',
    tag: 'Agile-ready', dark: false,
  },
  {
    icon: '🕐', title: 'Attendance & Leave',
    desc: 'Real-time clock-in/out, shift scheduling, leave requests, and balance tracking — seamlessly linked to your projects and timesheets so nothing falls through.',
    tag: 'Always in sync', dark: false,
  },
];

const steps = [
  { num: '01', title: 'Set up your workspace', desc: 'Create your organisation, invite your team, and configure departments and roles in under 10 minutes — no IT ticket needed.' },
  { num: '02', title: 'Create projects & assign work', desc: 'Build projects, break them into tasks, assign owners, set deadlines, and connect everything to sprints or timesheets instantly.' },
  { num: '03', title: 'Track progress in real time', desc: 'Live dashboards show you where every project stands, how resources are allocated, and where blockers are forming — before they become problems.' },
  { num: '04', title: 'Optimise and ship faster', desc: 'Use utilisation reports, sprint velocity trends, and AI nudges to continuously improve how your team works and deliver more, with less friction.' },
];

const flowNodes = [
  { ico: '🏢', label: 'Create Workspace', hint: 'Org · Departments · Teams' },
  { ico: '📁', label: 'Add Projects', hint: 'Milestones · Phases · Goals' },
  { ico: '✅', label: 'Assign Tasks & Sprints', hint: 'Owners · Deadlines · Priority' },
  { ico: '⏱️', label: 'Log Time & Track Work', hint: 'Timesheets · Attendance · Progress' },
  { ico: '🚀', label: 'Ship & Improve', hint: 'Avg. setup time: 10 minutes' },
];

const testimonials = [
  {
    text: '"Balsuite replaced five different tools we were juggling. Timesheets, tasks, sprints, and resource planning are all connected now — our team ships 30% faster and with far less chaos."',
    name: 'Arjun Menon', role: 'Engineering Manager, Stackify', initials: 'AM', bg: '#7F77DD', dark: false,
  },
  {
    text: '"The resource utilisation view alone changed how we plan quarters. We stopped overloading our senior devs and finally have a clear picture of capacity before we commit to a sprint."',
    name: 'Sana Qureshi', role: 'Head of Product, Lumos Labs', initials: 'SQ', bg: '#00c27a', dark: true,
  },
  {
    text: '"Sprint management in Balsuite is cleaner than Jira and simpler than Linear. Burndown charts, backlog grooming, and retrospectives in one place — our PMs love it."',
    name: 'Dev Kapoor', role: 'CTO, Nexora Technologies', initials: 'DK', bg: '#D85A30', dark: false,
  },
];

const plans = [
  {
    tier: 'Free', price: '₹0', mo: '/forever',
    desc: 'For small teams getting started with smarter work management.',
    features: ['Up to 10 members', 'Task & Work Management', 'Basic Timesheets', '3 Active Projects', 'Community Support'],
    btn: 'Get started free', btnClass: 'p-btn p-btn-border', pop: false,
  },
  {
    tier: 'Pro', price: '₹599', mo: '/user/mo',
    desc: 'For growing teams that need full project, sprint, and resource control.',
    features: ['Unlimited members', 'Sprint Management', 'Resource Utilization', 'Advanced Timesheets', 'Priority Support', 'Custom Workflows'],
    btn: 'Start free trial', btnClass: 'p-btn p-btn-white', pop: true, badge: 'Most popular',
  },
  {
    tier: 'Enterprise', price: 'Custom', mo: '',
    desc: 'For large organisations needing deep integrations, compliance, and scale.',
    features: ['Unlimited everything', 'SSO & API access', 'Dedicated CSM', 'SLA guarantee', 'On-prem option', 'Advanced analytics'],
    btn: 'Contact sales', btnClass: 'p-btn p-btn-border', pop: false,
  },
];

const kpis = [
  { label: 'Active Projects', val: '48', trend: '↑ 6 this week', type: 'up' },
  { label: 'Tasks In Progress', val: '312', trend: '↓ 24 resolved today', type: 'up' },
  { label: 'Sprint Velocity', val: '87%', trend: '↑ 4% vs last sprint', type: 'up' },
  { label: 'Team Utilization', val: '78%', trend: 'Optimal range', type: 'flat' },
];

const navItems = [
  { icon: '📊', label: 'Dashboard', badge: null, active: true },
  { icon: '📁', label: 'Projects', badge: null, active: false },
  { icon: '✅', label: 'My Tasks', badge: '8', active: false },
  { icon: '🏃', label: 'Sprints', badge: '2', active: false },
  { icon: '⏱️', label: 'Timesheets', badge: null, active: false },
  { icon: '📊', label: 'Resources', badge: null, active: false },
  { icon: '🕐', label: 'Attendance', badge: '3', active: false },
];

const tableRows = [
  { name: 'Arjun Nair', project: 'Balsuite v2.0', status: 'active', hours: '6.5h', bg: '#7F77DD' },
  { name: 'Sneha Iyer', project: 'Mobile App', status: 'remote', hours: '4.0h', bg: '#00c27a' },
  { name: 'Vikram Das', project: 'API Gateway', status: 'leave', hours: '0.0h', bg: '#D85A30' },
  { name: 'Pooja Nath', project: 'Design System', status: 'active', hours: '7.2h', bg: '#0ea5e9' },
];

const progressBars = [
  { label: 'Balsuite v2.0', pct: 72, color: '#00c27a' },
  { label: 'Mobile App', pct: 45, color: '#0ea5e9' },
  { label: 'API Gateway', pct: 88, color: '#8b5cf6' },
];

const sprintBars = [62, 78, 55, 90, 72, 85, 94, 87, 91, 97];

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function Navbar() {
  return (
    <nav>
      <div className="nav-inner">
       <img src={logo} alt="Balsuite" className="nav-logo" />
        <div className="nav-links">
          {['Features', 'Solutions', 'Pricing', 'Blog'].map(l => <a key={l} href="#">{l}</a>)}
        </div>
        <div className="nav-cta">
          <button className="btn-ghost">Log in</button>
          <button className="btn-nav-primary">Get started free →</button>
        </div>
      </div>
    </nav>
  );
}

function StatusPill({ status }) {
  const map = { active: ['s-active', 'Active'], leave: ['s-leave', 'On Leave'], remote: ['s-remote', 'Remote'] };
  const [cls, label] = map[status] || ['s-active', status];
  return <span className={`s-pill ${cls}`}>{label}</span>;
}

function DashboardPanel() {
  return (
    <div className="hero-panel-wrap">
      <div className="hero-panel">
        <div className="hp-bar">
          <div className="hpd hpd-r" /><div className="hpd hpd-y" /><div className="hpd hpd-g" />
          <div className="hp-url">app.balsuite.io/workspace</div>
        </div>
        <div className="hp-body">
          {/* Sidebar */}
          <div className="hp-sidebar">
            <div className="hp-nav-section">Workspace</div>
            {navItems.map(n => (
              <div className={`hp-nav-item${n.active ? ' active' : ''}`} key={n.label}>
                <span className="hp-nav-emoji">{n.icon}</span>
                {n.label}
                {n.badge && <span className="hp-badge">{n.badge}</span>}
              </div>
            ))}
          </div>
          {/* Main */}
          <div className="hp-main">
            {/* KPIs */}
            <div className="hp-kpi-row">
              {kpis.map(k => (
                <div className="hp-kpi" key={k.label}>
                  <div className="hp-kpi-label">{k.label}</div>
                  <div className="hp-kpi-val">{k.val}</div>
                  <div className={`hp-kpi-trend trend-${k.type}`}>{k.trend}</div>
                </div>
              ))}
            </div>
            {/* Mid row */}
            <div className="hp-mid">
              <div className="hp-box">
                <div className="hp-box-title">Sprint Velocity — Last 10 Sprints</div>
                <div className="att-bars">
                  {sprintBars.map((h, i) => (
                    <div key={i} className={`att-bar${h >= 88 ? ' hi' : h >= 75 ? ' mid' : ''}`} style={{ height: `${h}%` }} />
                  ))}
                </div>
                <div className="att-days">{['S1','S2','S3','S4','S5','S6','S7','S8','S9','S10'].map((d, i) => <span key={i}>{d}</span>)}</div>
              </div>
              <div className="hp-box">
                <div className="hp-box-title">Project Progress</div>
                <div className="leave-rows">
                  {progressBars.map(p => (
                    <div className="leave-row" key={p.label}>
                      <div className="leave-info">
                        <div className="leave-dot" style={{ background: p.color }} />
                        <span className="leave-type">{p.label}</span>
                      </div>
                      <div className="leave-bar-wrap">
                        <div className="leave-fill" style={{ width: `${p.pct}%`, background: p.color }} />
                      </div>
                      <span className="leave-pct">{p.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Team activity table */}
            <div className="hp-table">
              <div className="hp-table-hd">
                <span>Team Member</span><span>Project</span><span>Hours Today</span><span>Status</span>
              </div>
              {tableRows.map(row => (
                <div className="hp-table-row" key={row.name}>
                  <div className="hp-cell hp-cell-emp">
                    <div className="emp-avi" style={{ background: row.bg }}>{row.name.split(' ').map(n => n[0]).join('')}</div>
                    <span className="emp-name">{row.name}</span>
                  </div>
                  <div className="hp-cell">{row.project}</div>
                  <div className="hp-cell">{row.hours}</div>
                  <div className="hp-cell"><StatusPill status={row.status} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-glow-1" /><div className="hero-glow-2" /><div className="hero-grid-bg" />
      <div className="hero-inner">
        <div className="hero-content">
          <div className="hero-eyebrow">
            <span className="eyebrow-pill">New</span>
            <span className="eyebrow-dot" />
            Introducing Sprint &amp; Resource Management
          </div>
          <h1>Simplify Work,<br /><em>Amplify Results.</em></h1>
          <p className="hero-desc">
            Experience a simpler, smarter way to manage your work with <strong style={{ color: '#fff', fontWeight: 600 }}>BALSUITE</strong> — projects, sprints, timesheets, tasks, and resource utilisation unified in one powerful workspace for your entire company.
          </p>
          <div className="hero-actions">
            <button className="btn-hero-primary">Start for free →</button>
            <button className="btn-hero-outline">
              <span className="play-icon">▶</span>
              Watch demo
            </button>
          </div>
          <div className="hero-stats">
            {[
              { val: '10K+', label: 'Teams powered' },
              null,
              { val: '2M+', label: 'Tasks completed' },
              null,
              { val: '99.9%', label: 'Uptime SLA' },
            ].map((s, i) =>
              s === null
                ? <div className="hero-stat-div" key={i} />
                : <div className="hero-stat" key={s.val}>
                    <div className="hero-stat-val">{s.val}</div>
                    <div className="hero-stat-label">{s.label}</div>
                  </div>
            )}
          </div>
        </div>
      </div>
      <DashboardPanel />
    </section>
  );
}

function Logos() {
  const companies = ['Atlassian', 'Notion', 'Freshworks', 'Razorpay', 'Zoho', 'Groww'];
  return (
    <section className="logos">
      <div className="logos-inner">
        <span className="logos-label">Trusted by teams at</span>
        <div className="logos-sep" />
        <div className="logos-row">
          {companies.map(c => <span className="logo-mark" key={c}>{c}</span>)}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="features">
      <div className="features-inner">
        <div className="features-header">
          <div>
            <span className="sec-eyebrow">Everything you need</span>
            <h2 className="sec-h2">Every module your team<br />needs, <em>all connected</em></h2>
            <p className="sec-sub">From onboarding your first hire to shipping your hundredth sprint — Balsuite connects every part of how your company works.</p>
          </div>
          <div style={{ maxWidth: '280px' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: '1.65' }}>
              Every module is deeply connected — log time against a task, which links to a sprint, which rolls up to a project, which feeds your resource view. Automatically.
            </p>
          </div>
        </div>
        <div className="feat-grid feat-grid-7">
          {features.map(f => (
            <div className={`feat-card${f.dark ? ' dark' : ''}`} key={f.title}>
              <div className="feat-icon">{f.icon}</div>
              <div className="feat-title">{f.title}</div>
              <p className="feat-desc">{f.desc}</p>
              <span className="feat-tag">{f.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="how">
      <div className="how-inner">
        <div className="how-left">
          <span className="sec-eyebrow">How it works</span>
          <h2 className="sec-h2">From setup to shipping<br />in <em>minutes</em></h2>
          <p className="sec-sub">No lengthy onboarding. No consultants. Your entire work management system goes live in a single afternoon.</p>
          <div className="how-steps">
            {steps.map((s, i) => (
              <div className="how-step" key={s.num}>
                <div className="step-circle">{s.num}</div>
                <div className="step-body" style={{ paddingBottom: i === steps.length - 1 ? 0 : undefined }}>
                  <div className="step-title">{s.title}</div>
                  <p className="step-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="how-right">
          <div className="how-card">
            <div className="how-card-title">Workflow Setup</div>
            <div className="pf-nodes">
              {flowNodes.map((n, i) => (
                <div key={i}>
                  <div className="pf-node">
                    <div className="pf-ico">{n.ico}</div>
                    <div>
                      <div className="pf-label">{n.label}</div>
                      <div className="pf-hint">{n.hint}</div>
                    </div>
                    <div className="pf-check">✓</div>
                  </div>
                  {i < flowNodes.length - 1 && <div className="pf-line" />}
                </div>
              ))}
            </div>
            <div className="how-card-footer">
              {[
                { val: '10 min', lbl: 'Avg. setup time' },
                null,
                { val: '40%', lbl: 'Faster delivery' },
                null,
                { val: '0', lbl: 'Tools replaced' },
              ].map((s, i) =>
                s === null
                  ? <div className="hcf-sep" key={i} />
                  : <div className="hcf-stat" key={s.lbl}>
                      <div className="hcf-val">{s.val}</div>
                      <div className="hcf-lbl">{s.lbl}</div>
                    </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testimonials">
      <div className="testi-inner">
        <div className="testi-header">
          <div>
            <span className="sec-eyebrow">Testimonials</span>
            <h2 className="sec-h2">Teams work <em>better</em> with Balsuite</h2>
          </div>
          <p style={{ fontSize: '0.9rem', color: 'var(--muted)', maxWidth: '260px', lineHeight: '1.65' }}>
            Over 10,000 teams worldwide use Balsuite to simplify their work and ship results faster.
          </p>
        </div>
        <div className="testi-grid">
          {testimonials.map(t => (
            <div className={`testi-card${t.dark ? ' dark' : ''}`} key={t.name}>
              <div className="testi-stars">★★★★★</div>
              <p className="testi-text">{t.text}</p>
              <div className="testi-author">
                <div className="testi-avi" style={{ background: t.bg }}>{t.initials}</div>
                <div>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section className="pricing">
      <div className="pricing-inner">
        <div className="pricing-header">
          <span className="sec-eyebrow">Pricing</span>
          <h2 className="sec-h2">Simple, <em>transparent</em> pricing</h2>
          <p className="sec-sub">Start free. Upgrade when you're ready. No hidden charges, ever.</p>
          <div className="ptoggle" style={{ display: 'flex' }}>
            <button className="ptog on">Monthly</button>
            <button className="ptog">Annually <span className="save-tag">Save 25%</span></button>
          </div>
        </div>
        <div className="pricing-grid">
          {plans.map(plan => (
            <div className={`p-card${plan.pop ? ' pop' : ''}`} key={plan.tier}>
              {plan.pop && <div className="pop-label">{plan.badge}</div>}
              <div className="p-tier">{plan.tier}</div>
              <div>
                <span className="p-price">{plan.price}</span>
                {plan.mo && <span className="p-mo">{plan.mo}</span>}
              </div>
              <p className="p-desc">{plan.desc}</p>
              <ul className="p-feats">
                {plan.features.map(f => (
                  <li key={f}><span className="p-check">✓</span> {f}</li>
                ))}
              </ul>
              <button className={plan.btnClass}>{plan.btn}</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="cta">
      <div className="cta-inner">
        <h2>Ready to simplify how<br /><em>your team works?</em></h2>
        <p className="cta-sub">Join 10,000+ teams already using Balsuite to cut the chaos, ship faster, and amplify their results.</p>
        <div className="cta-btns">
          <button className="btn-cta-green">Start for free →</button>
          <button className="btn-cta-ghost">See a live demo</button>
        </div>
        <p className="cta-note">Free plan available · No credit card required · Cancel anytime</p>
        <div className="cta-feats">
          {['SOC 2 Compliant', 'GDPR Ready', '99.9% Uptime', 'Free Migrations'].map(f => (
            <div className="cta-feat" key={f}><div className="cta-feat-dot" />{f}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const cols = [
    { heading: 'Product', links: ['Features', 'Pricing', 'Changelog', 'Roadmap', 'Integrations'] },
    { heading: 'Company', links: ['About Us', 'Careers', 'Blog', 'Press', 'Partners'] },
    { heading: 'Support', links: ['Help Centre', 'Contact Us', 'Status', 'Community', 'Security'] },
  ];
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={logo} alt="Balsuite" className="nav-logo" />
            <p className="footer-tagline">Simplify Work, Amplify Results. The smarter work management platform for your entire company.</p>
            <div className="footer-social">
              {['𝕏', 'in', 'yt', 'gh'].map(s => <div className="soc-btn" key={s}>{s}</div>)}
            </div>
          </div>
          {cols.map(col => (
            <div className="footer-col" key={col.heading}>
              <h4>{col.heading}</h4>
              <ul>
                {col.links.map(l => <li key={l}><a href="#">{l}</a></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          <p className="footer-copy">© 2025 Balsuite Technologies Pvt. Ltd. All rights reserved.</p>
          <div className="footer-legal">
            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map(l => <a href="#" key={l}>{l}</a>)}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function BalsuiteApp() {
  return (
    <div className="balsuite-page">
      <Navbar />
      <Hero />
      <Logos />
      <Features />
      <HowItWorks />
      <Testimonials />
      <Pricing />
      <CTA />
      <Footer />
    </div>
  );
}